import React, { useState, useEffect, useCallback } from 'react';

const MANAGE_CSS = `
  .mp-input:focus { border-color: #0ea5e9 !important; box-shadow: 0 0 0 3px rgba(14,165,233,0.18) !important; outline: none; }
  .mp-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(14,165,233,0.45); }
  .mp-btn-primary { transition: all 0.18s cubic-bezier(.4,0,.2,1); }
  .mp-card-in { animation: mpFadeUp 0.45s cubic-bezier(.4,0,.2,1) both; }
  @keyframes mpFadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .topic-pill { transition: all 0.15s cubic-bezier(.4,0,.2,1); }
  .topic-pill:hover { transform: translateY(-2px) scale(1.03); }
  .mp-section { animation: mpFadeUp 0.4s cubic-bezier(.4,0,.2,1) both; }
  .otp-input { letter-spacing: 8px; font-size: 28px; text-align: center; }
  .otp-input:focus { border-color: #0ea5e9 !important; box-shadow: 0 0 0 3px rgba(14,165,233,0.18) !important; outline: none; }
  .save-success { animation: mpFadeUp 0.3s ease both; }
  .plan-card { transition: all 0.18s cubic-bezier(.4,0,.2,1); }
  .plan-card:hover:not(:disabled) { transform: translateY(-2px); }
`;

/* ─── Data ────────────────────────────────────────────────────────── */
const TOPIC_GROUPS = [
  { label: 'Finance & Business', color: '#22c55e', topics: [
    { name: 'Finance', emoji: '💰' }, { name: 'Crypto', emoji: '₿' },
    { name: 'Business', emoji: '📈' }, { name: 'Startups', emoji: '🦄' },
    { name: 'Real Estate', emoji: '🏠' },
  ]},
  { label: 'Health & Wellness', color: '#f43f5e', topics: [
    { name: 'Health', emoji: '🏥' }, { name: 'Fitness', emoji: '💪' },
    { name: 'Mental Health', emoji: '🧠' }, { name: 'Nutrition', emoji: '🥗' },
    { name: 'Self-Improvement', emoji: '🌱' }, { name: 'Parenting', emoji: '👶' },
  ]},
  { label: 'Tech & Science', color: '#6366f1', topics: [
    { name: 'AI', emoji: '🤖' }, { name: 'Tech', emoji: '💻' },
    { name: 'Science', emoji: '🔬' }, { name: 'Space', emoji: '🚀' },
    { name: 'Gaming', emoji: '🎮' }, { name: 'Automotive', emoji: '🚗' },
  ]},
  { label: 'Culture & Lifestyle', color: '#f97316', topics: [
    { name: 'Entertainment', emoji: '🎬' }, { name: 'Sports', emoji: '🏆' },
    { name: 'Travel', emoji: '✈️' }, { name: 'Food', emoji: '🍕' },
    { name: 'Design', emoji: '🎨' }, { name: 'Fashion', emoji: '👗' },
    { name: 'Music', emoji: '🎵' },
  ]},
  { label: 'Knowledge & World', color: '#a855f7', topics: [
    { name: 'Philosophy', emoji: '📚' }, { name: 'World News', emoji: '🗞️' },
    { name: 'Climate', emoji: '🌍' }, { name: 'Productivity', emoji: '⚡' },
    { name: 'History', emoji: '🏛️' }, { name: 'Photography', emoji: '📷' },
  ]},
];

const TIME_PRESETS = [
  { label: '6:00 AM',  value: '06:00', icon: '🌅' },
  { label: '7:00 AM',  value: '07:00', icon: '☀️' },
  { label: '8:00 AM',  value: '08:00', icon: '☕', badge: true },
  { label: '9:00 AM',  value: '09:00', icon: '🚀' },
  { label: '12:00 PM', value: '12:00', icon: '🌤️' },
  { label: '6:00 PM',  value: '18:00', icon: '🌆' },
  { label: '9:00 PM',  value: '21:00', icon: '🌙' },
  { label: 'Custom',   value: 'custom', icon: '🎛️' },
];

const TIMEZONES = [
  { label: 'Eastern Time (ET)',    value: 'America/New_York' },
  { label: 'Central Time (CT)',    value: 'America/Chicago' },
  { label: 'Mountain Time (MT)',   value: 'America/Denver' },
  { label: 'Pacific Time (PT)',    value: 'America/Los_Angeles' },
  { label: 'Alaska Time (AKT)',    value: 'America/Anchorage' },
  { label: 'Hawaii Time (HT)',     value: 'Pacific/Honolulu' },
  { label: 'London (GMT/BST)',     value: 'Europe/London' },
  { label: 'Paris / Berlin (CET)', value: 'Europe/Paris' },
  { label: 'Dubai (GST)',          value: 'Asia/Dubai' },
  { label: 'Mumbai (IST)',         value: 'Asia/Kolkata' },
  { label: 'Singapore (SGT)',      value: 'Asia/Singapore' },
  { label: 'Tokyo (JST)',          value: 'Asia/Tokyo' },
  { label: 'Sydney (AEST)',        value: 'Australia/Sydney' },
  { label: 'São Paulo (BRT)',      value: 'America/Sao_Paulo' },
  { label: 'Mexico City (CST)',    value: 'America/Mexico_City' },
  { label: 'Toronto (ET)',         value: 'America/Toronto' },
  { label: 'Vancouver (PT)',       value: 'America/Vancouver' },
];

const PLANS = [
  {
    id: 'basic', name: 'Basic', price: '$5', period: '/mo', links: 5,
    desc: '5 hand-picked links delivered daily.',
    features: ['5 links per day', 'All 30 topics', 'Custom schedule'],
    color: '#3b82f6',
  },
  {
    id: 'pro', name: 'Pro', price: '$8', period: '/mo', links: 10,
    desc: '10 curated links for the power reader.',
    features: ['10 links per day', 'All 30 topics', 'Custom schedule', 'Priority sources'],
    color: '#7c3aed', badge: 'Best value',
  },
];

function formatDisplayTime(val) {
  if (!val) return '8:00 AM';
  const [hh, mm] = val.split(':');
  const h = parseInt(hh, 10);
  return `${h % 12 === 0 ? 12 : h % 12}:${mm} ${h >= 12 ? 'PM' : 'AM'}`;
}

const MAX_TOPICS = 6;
const TOKEN_KEY = 'dd_session_token';
const API_BASE  = process.env.REACT_APP_API_URL || '';

/* ─── Main component ──────────────────────────────────────────────── */
export default function ManageProfile({ onBack }) {
  const [view, setView]       = useState('loading');
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState('');
  const [token, setToken]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  // Settings
  const [profileName, setProfileName]   = useState('');
  const [topics, setTopics]             = useState([]);
  const [deliveryTime, setDeliveryTime] = useState('08:00');
  const [customTime, setCustomTime]     = useState('');
  const [timezone, setTimezone]         = useState('America/New_York');
  const [pausedUntil, setPausedUntil]   = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [history, setHistory]           = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Plan / subscription
  const [tier, setTier]                   = useState(null);
  const [stripeStatus, setStripeStatus]   = useState(null);
  const [planLoading, setPlanLoading]     = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // UI
  const [activeSection, setActiveSection] = useState('topics');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) { setToken(stored); loadProfile(stored); }
    else setView('email');
  }, []); // eslint-disable-line

  const loadProfile = useCallback(async (tok) => {
    try {
      const res = await fetch(`${API_BASE}/api/profile`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.status === 401) { localStorage.removeItem(TOKEN_KEY); setView('email'); return; }
      const data = await res.json();
      setProfileName(data.name || '');
      setTopics(data.topics || []);
      const preset = TIME_PRESETS.find(p => p.value === data.delivery_time);
      setDeliveryTime(preset ? data.delivery_time : 'custom');
      if (!preset) setCustomTime(data.delivery_time || '08:00');
      setTimezone(data.timezone || 'America/New_York');
      setPausedUntil(data.paused_until || null);
      setEmail(data.email || '');
      setTier(data.tier || null);
      setStripeStatus(data.stripe_status || null);
      setView('settings');
    } catch { setView('email'); }
  }, []);

  async function handleRequestOTP() {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setView('otp');
    } catch { setError('Unable to connect.'); }
    finally { setLoading(false); }
  }

  async function handleVerifyOTP() {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      await loadProfile(data.token);
    } catch { setError('Unable to connect.'); }
    finally { setLoading(false); }
  }

  async function handleSave() {
    setError(''); setSaveMsg(''); setLoading(true);
    const finalTime = deliveryTime === 'custom' ? customTime || '08:00' : deliveryTime;
    try {
      const res = await fetch(`${API_BASE}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: profileName, topics, delivery_time: finalTime, timezone }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setSaveMsg('Settings saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch { setError('Save failed. Check your connection.'); }
    finally { setLoading(false); }
  }

  async function handlePause(days) {
    setPauseLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/profile/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ days }),
      });
      const data = await res.json();
      if (res.ok) {
        setPausedUntil(days > 0 ? new Date(Date.now() + days * 86400000).toISOString() : null);
        setSaveMsg(days > 0 ? `Paused for ${days} day${days > 1 ? 's' : ''}.` : 'Resumed! Digests will continue tomorrow.');
        setTimeout(() => setSaveMsg(''), 3000);
      } else { setError(data.error); }
    } catch { setError('Failed to update pause.'); }
    finally { setPauseLoading(false); }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/profile/history`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setHistory(data.history || []);
    } catch {}
    finally { setHistoryLoading(false); }
  }

  async function handleLogout() {
    try { await fetch(`${API_BASE}/api/profile/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setToken(''); setView('email'); setEmail(''); setOtp('');
  }

  async function handleDeleteAccount() {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/profile`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem(TOKEN_KEY);
      setView('deleted');
    } catch { setError('Deletion failed. Please try again.'); }
    finally { setLoading(false); }
  }

  async function handleChangePlan(newTier) {
    setPlanLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/profile/change-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: newTier }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to change plan.'); return; }
      setTier(newTier);
      setSaveMsg(`Switched to ${newTier === 'pro' ? 'Pro' : 'Basic'}! Changes take effect immediately.`);
      setTimeout(() => setSaveMsg(''), 4000);
    } catch { setError('Unable to connect. Please try again.'); }
    finally { setPlanLoading(false); }
  }

  async function handleCancelSubscription() {
    setCancelLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/profile/cancel-subscription`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setTier(null); setStripeStatus('cancelled'); setShowCancelConfirm(false);
        setSaveMsg('Subscription cancelled. Access continues until end of billing period.');
        setTimeout(() => setSaveMsg(''), 5000);
      } else { setError(data.error || 'Failed to cancel.'); }
    } catch { setError('Unable to connect. Please try again.'); }
    finally { setCancelLoading(false); }
  }

  function toggleTopic(name) {
    setTopics(prev => {
      if (prev.includes(name)) return prev.filter(t => t !== name);
      if (prev.length >= MAX_TOPICS) return prev;
      return [...prev, name];
    });
  }

  /* ── Loading ── */
  if (view === 'loading') return <div style={m.page}><div style={{ color: '#475569', fontSize: 16 }}>Loading…</div></div>;

  /* ── Deleted ── */
  if (view === 'deleted') return (
    <><style>{MANAGE_CSS}</style>
    <div style={m.page}>
      <div className="mp-card-in" style={{ ...m.card, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 20 }}>👋</div>
        <h2 style={m.cardTitle}>Account deleted</h2>
        <p style={m.cardSub}>Your subscription and data have been removed. Sorry to see you go.</p>
        <button className="mp-btn-primary" onClick={onBack} style={m.primaryBtn}>← Back to home</button>
      </div>
    </div></>
  );

  /* ── Email entry ── */
  if (view === 'email') {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    return (
      <><style>{MANAGE_CSS}</style>
      <div style={m.page}>
        <div style={m.orb1} /><div style={m.orb2} />
        <div className="mp-card-in" style={m.card}>
          <button onClick={onBack} style={m.backLink}>← Home</button>
          <div style={{ fontSize: 48, marginBottom: 16 }}>💧</div>
          <h1 style={m.cardTitle}>Manage your subscription</h1>
          <p style={m.cardSub}>Enter your email and we'll send a verification code.</p>
          <div style={m.fieldGroup}>
            <label style={m.label}>Email address</label>
            <input className="mp-input" type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && emailValid && handleRequestOTP()}
              style={m.input} autoFocus />
          </div>
          {error && <div style={m.errorBox}>{error}</div>}
          <button className="mp-btn-primary" onClick={handleRequestOTP} disabled={loading || !emailValid}
            style={{ ...m.primaryBtn, opacity: loading || !emailValid ? 0.4 : 1 }}>
            {loading ? 'Sending…' : 'Send verification code →'}
          </button>
          <p style={m.legalNote}>Don't have an account? <button onClick={onBack} style={m.textLink}>Sign up here</button></p>
        </div>
      </div></>
    );
  }

  /* ── OTP ── */
  if (view === 'otp') return (
    <><style>{MANAGE_CSS}</style>
    <div style={m.page}>
      <div style={m.orb1} /><div style={m.orb2} />
      <div className="mp-card-in" style={m.card}>
        <button onClick={() => { setView('email'); setError(''); setOtp(''); }} style={m.backLink}>← Back</button>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
        <h1 style={m.cardTitle}>Check your inbox</h1>
        <p style={m.cardSub}>We sent a 6-digit code to <strong style={{ color: '#38bdf8' }}>{email}</strong>. Expires in 10 minutes.</p>
        <div style={m.fieldGroup}>
          <input className="mp-input otp-input" type="text" inputMode="numeric" pattern="[0-9]*"
            placeholder="000000" value={otp} maxLength={6}
            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={e => e.key === 'Enter' && otp.length === 6 && handleVerifyOTP()}
            style={{ ...m.input, letterSpacing: 10, fontSize: 28, textAlign: 'center' }} />
        </div>
        {error && <div style={m.errorBox}>{error}</div>}
        <button className="mp-btn-primary" onClick={handleVerifyOTP} disabled={loading || otp.length < 6}
          style={{ ...m.primaryBtn, opacity: loading || otp.length < 6 ? 0.4 : 1 }}>
          {loading ? 'Verifying…' : 'Verify & access settings →'}
        </button>
        <p style={m.legalNote}>Didn't get it? <button onClick={() => { setView('email'); setOtp(''); setError(''); }} style={m.textLink}>Resend code</button></p>
      </div>
    </div></>
  );

  /* ── Settings dashboard ── */
  const finalTime = deliveryTime === 'custom' ? customTime || '08:00' : deliveryTime;
  const TABS = [
    { id: 'topics',  label: '🏷️ Topics' },
    { id: 'time',    label: '⏰ Delivery' },
    { id: 'plan',    label: '💳 Plan' },
    { id: 'history', label: '📖 History' },
    { id: 'account', label: '👤 Account' },
  ];

  return (
    <><style>{MANAGE_CSS}</style>
    <div style={m.dashPage}>
      <div style={m.orb1} /><div style={m.orb2} />

      {/* Header */}
      <div style={m.header}>
        <div style={m.headerLeft}>
          <span style={m.logo}>💧</span>
          <div>
            <div style={m.headerTitle}>DailyDroplet</div>
            <div style={m.headerSub}>Manage subscription</div>
          </div>
        </div>
        <div style={m.headerRight}>
          <span style={m.phoneBadge}>{email}</span>
          <button onClick={handleLogout} style={m.logoutBtn}>Sign out</button>
        </div>
      </div>

      <div style={m.dashBody}>
        {/* Welcome row */}
        <div style={m.welcomeRow}>
          <div>
            <h1 style={m.welcomeTitle}>{profileName ? `Welcome back, ${profileName}!` : 'Your settings'}</h1>
            <p style={m.welcomeSub}>
              Digest at <strong style={{ color: '#38bdf8' }}>{formatDisplayTime(finalTime)}</strong>
              {' · '}{topics.length} topic{topics.length !== 1 ? 's' : ''}
              {tier && <>{' · '}<span style={{ color: tier === 'pro' ? '#a78bfa' : '#60a5fa', fontWeight: 600 }}>{tier === 'pro' ? 'Pro' : 'Basic'}</span></>}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {saveMsg && <span className="save-success" style={m.saveSuccess}>{saveMsg}</span>}
            {(activeSection === 'topics' || activeSection === 'time' || activeSection === 'account') && (
              <button className="mp-btn-primary" onClick={handleSave} disabled={loading || topics.length === 0}
                style={{ ...m.saveBtn, opacity: loading || topics.length === 0 ? 0.4 : 1 }}>
                {loading ? 'Saving…' : '💾 Save changes'}
              </button>
            )}
          </div>
        </div>

        {error && <div style={{ ...m.errorBox, marginBottom: 24 }}>{error}</div>}

        {/* Tabs */}
        <div style={m.tabs}>
          {TABS.map(tab => (
            <button key={tab.id}
              onClick={() => { setActiveSection(tab.id); setError(''); if (tab.id === 'history') loadHistory(); }}
              style={{ ...m.tab, color: activeSection === tab.id ? '#38bdf8' : '#475569', borderBottom: activeSection === tab.id ? '2px solid #0ea5e9' : '2px solid transparent' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Topics ── */}
        {activeSection === 'topics' && (
          <div className="mp-section">
            <div style={m.sectionHeader}>
              <h2 style={m.sectionTitle}>Your topics</h2>
              <span style={{ ...m.sectionSub, color: topics.length >= MAX_TOPICS ? '#f59e0b' : '#475569', fontWeight: topics.length >= MAX_TOPICS ? 600 : 400 }}>
                {topics.length} / {MAX_TOPICS} selected
              </span>
            </div>
            {topics.length >= MAX_TOPICS && (
              <div style={m.capBanner}>
                Max 6 topics reached — tap one to deselect it before picking another.
              </div>
            )}
            {TOPIC_GROUPS.map(group => (
              <div key={group.label} style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: group.color, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: 0.8, textTransform: 'uppercase' }}>{group.label}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {group.topics.map(({ name: tName, emoji }) => {
                    const sel = topics.includes(tName);
                    const disabled = !sel && topics.length >= MAX_TOPICS;
                    return (
                      <button key={tName} type="button" className="topic-pill"
                        onClick={() => { if (!disabled) toggleTopic(tName); }}
                        disabled={disabled}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '8px 14px', borderRadius: 999,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                          background: sel ? group.color + '22' : '#0a1628',
                          border: `1.5px solid ${sel ? group.color : disabled ? '#1e293b' : '#1e3a5f'}`,
                          color: sel ? group.color : disabled ? '#1e3a5f' : '#64748b',
                          opacity: disabled ? 0.35 : 1,
                        }}>
                        <span>{emoji}</span><span>{tName}</span>
                        {sel && <span style={{ fontSize: 10, fontWeight: 700 }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Delivery time ── */}
        {activeSection === 'time' && (
          <div className="mp-section">
            <div style={m.sectionHeader}>
              <h2 style={m.sectionTitle}>Delivery time</h2>
              <span style={m.sectionSub}>Currently: <strong style={{ color: '#38bdf8' }}>{formatDisplayTime(finalTime)}</strong></span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
              {TIME_PRESETS.map(preset => {
                const sel = deliveryTime === preset.value;
                return (
                  <button key={preset.value} type="button" onClick={() => setDeliveryTime(preset.value)}
                    style={{
                      borderRadius: 12, padding: '16px 12px', textAlign: 'center',
                      fontFamily: 'inherit', cursor: 'pointer', position: 'relative', overflow: 'hidden',
                      background: sel ? 'rgba(14,165,233,0.12)' : '#0a1628',
                      border: `1.5px solid ${sel ? '#0ea5e9' : '#1e3a5f'}`,
                      transition: 'all 0.15s',
                    }}>
                    {preset.badge && <div style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, color: '#f59e0b', fontWeight: 700 }}>⭐</div>}
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{preset.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: sel ? '#38bdf8' : '#f1f5f9' }}>{preset.label}</div>
                  </button>
                );
              })}
            </div>
            {deliveryTime === 'custom' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                <label style={m.label}>Custom time</label>
                <input className="mp-input" type="time" value={customTime}
                  onChange={e => setCustomTime(e.target.value)}
                  style={{ ...m.input, width: 180 }} />
              </div>
            )}
            <div style={{ marginTop: 24 }}>
              <label style={{ ...m.label, display: 'block', marginBottom: 12 }}>Time zone</label>
              <select className="mp-input" value={timezone} onChange={e => setTimezone(e.target.value)}
                style={{ ...m.input, maxWidth: 360 }}>
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* ── Plan ── */}
        {activeSection === 'plan' && (
          <div className="mp-section">
            <div style={m.sectionHeader}>
              <h2 style={m.sectionTitle}>Your plan</h2>
              {stripeStatus && (
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                  background: stripeStatus === 'active' ? 'rgba(34,197,94,0.12)' : stripeStatus === 'promo' ? 'rgba(14,165,233,0.12)' : 'rgba(239,68,68,0.1)',
                  color: stripeStatus === 'active' ? '#22c55e' : stripeStatus === 'promo' ? '#38bdf8' : '#f87171',
                  border: `1px solid ${stripeStatus === 'active' ? 'rgba(34,197,94,0.25)' : stripeStatus === 'promo' ? 'rgba(14,165,233,0.25)' : 'rgba(239,68,68,0.2)'}`,
                }}>
                  {stripeStatus === 'active' ? '● Active' : stripeStatus === 'promo' ? '● Promo' : '● Cancelled'}
                </span>
              )}
            </div>

            {/* Plan cards */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
              {PLANS.map(p => {
                const isCurrent = tier === p.id;
                const canSwitch = (stripeStatus === 'active' || stripeStatus === 'promo') && !isCurrent;
                return (
                  <div key={p.id} style={{
                    flex: 1, minWidth: 240, borderRadius: 18, padding: '24px 22px',
                    background: isCurrent ? `${p.color}14` : '#0a1628',
                    border: `2px solid ${isCurrent ? p.color : '#1e3a5f'}`,
                    position: 'relative', transition: 'all 0.15s',
                  }}>
                    {p.badge && (
                      <div style={{ position: 'absolute', top: 14, right: 14, background: p.color, color: '#fff', borderRadius: 6, padding: '2px 9px', fontSize: 10, fontWeight: 700 }}>{p.badge}</div>
                    )}
                    {isCurrent && (
                      <div style={{ position: 'absolute', top: 14, left: 14, background: p.color, color: '#fff', borderRadius: 6, padding: '2px 9px', fontSize: 10, fontWeight: 700 }}>Current plan</div>
                    )}
                    <div style={{ marginTop: isCurrent || p.badge ? 28 : 0 }}>
                      <div style={{ fontSize: 30, fontWeight: 800, color: isCurrent ? p.color : '#f1f5f9', marginBottom: 2 }}>
                        {p.price}<span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{p.period}</span>
                      </div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: isCurrent ? p.color : '#f1f5f9', marginBottom: 6 }}>{p.name}</div>
                      <div style={{ fontSize: 13, color: '#475569', marginBottom: 16, lineHeight: 1.5 }}>{p.desc}</div>
                      <ul style={{ margin: '0 0 20px', padding: 0, listStyle: 'none' }}>
                        {p.features.map(f => (
                          <li key={f} style={{ fontSize: 13, color: '#94a3b8', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: p.color, fontWeight: 900, fontSize: 11 }}>✓</span> {f}
                          </li>
                        ))}
                      </ul>
                      {canSwitch && (
                        <button className="plan-card"
                          onClick={() => handleChangePlan(p.id)}
                          disabled={planLoading}
                          style={{
                            width: '100%', padding: '11px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                            background: `${p.color}22`, border: `1.5px solid ${p.color}`, color: p.color,
                            borderRadius: 10, cursor: planLoading ? 'not-allowed' : 'pointer',
                            opacity: planLoading ? 0.5 : 1, transition: 'all 0.15s',
                          }}>
                          {planLoading ? 'Switching…' : `Switch to ${p.name}`}
                        </button>
                      )}
                      {isCurrent && (
                        <div style={{ fontSize: 13, color: p.color, fontWeight: 600, textAlign: 'center' }}>✓ Your current plan</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Cancel subscription */}
            {(stripeStatus === 'active') && (
              <div style={{ borderTop: '1px solid #1e3a5f', paddingTop: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>Cancel subscription</div>
                {!showCancelConfirm ? (
                  <button onClick={() => setShowCancelConfirm(true)}
                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 }}>
                    Cancel my subscription
                  </button>
                ) : (
                  <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '20px 24px' }}>
                    <p style={{ fontSize: 14, color: '#f87171', marginBottom: 14, lineHeight: 1.6 }}>
                      Are you sure? Your digests will stop at the end of the current billing period.
                    </p>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={handleCancelSubscription} disabled={cancelLoading}
                        style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700 }}>
                        {cancelLoading ? 'Cancelling…' : 'Yes, cancel'}
                      </button>
                      <button onClick={() => setShowCancelConfirm(false)}
                        style={{ background: 'none', border: '1px solid #1e3a5f', color: '#64748b', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>
                        Keep subscription
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(stripeStatus === 'cancelled' || !stripeStatus) && (
              <div style={{ background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>💧</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>No active subscription</div>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Subscribe to start receiving your daily digest.</div>
                <button onClick={onBack} className="mp-btn-primary"
                  style={{ ...m.saveBtn, display: 'inline-block', padding: '12px 28px' }}>
                  Choose a plan →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── History ── */}
        {activeSection === 'history' && (
          <div className="mp-section">
            <div style={m.sectionHeader}>
              <h2 style={m.sectionTitle}>Digest history</h2>
              <span style={m.sectionSub}>Last 5 droplets</span>
            </div>
            {historyLoading ? (
              <div style={{ color: '#475569', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>Loading history…</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>No digests sent yet</div>
                <div style={{ fontSize: 14, color: '#475569' }}>Your history will appear here after your first delivery.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {history.map((entry, i) => (
                  <div key={entry.id || i} style={{ background: '#0a1628', border: '1px solid #1e3a5f', borderRadius: 14, padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>
                        📬 {new Date(entry.sent_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div style={{ fontSize: 11, color: '#334155' }}>
                        {new Date(entry.sent_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(entry.items || []).map((item, j) => (
                        <a key={j} href={item.url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, textDecoration: 'none', padding: '10px 14px', background: '#060d1f', borderRadius: 10, border: '1px solid #1e293b', transition: 'border-color 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#1e3a5f'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'}>
                          <span style={{ fontSize: 11, color: '#475569', background: '#0a1628', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 }}>{item.source}</span>
                          <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5, fontWeight: 500 }}>{item.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Account ── */}
        {activeSection === 'account' && (
          <div className="mp-section">
            <div style={m.sectionHeader}>
              <h2 style={m.sectionTitle}>Account</h2>
            </div>

            {/* Pause */}
            <div style={{ marginBottom: 32, background: '#0a1628', border: '1px solid #1e3a5f', borderRadius: 14, padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>⏸ Pause digests</div>
                  <div style={{ fontSize: 13, color: '#475569' }}>
                    {pausedUntil && new Date(pausedUntil) > new Date()
                      ? `Paused until ${new Date(pausedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                      : 'Currently delivering daily'}
                  </div>
                </div>
                {pausedUntil && new Date(pausedUntil) > new Date() && (
                  <button onClick={() => handlePause(0)} disabled={pauseLoading}
                    style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}>
                    {pauseLoading ? '…' : '▶ Resume now'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[1, 3, 7].map(days => (
                  <button key={days} onClick={() => handlePause(days)} disabled={pauseLoading}
                    style={{ background: '#060d1f', border: '1.5px solid #1e3a5f', color: '#94a3b8', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, opacity: pauseLoading ? 0.5 : 1, transition: 'all 0.15s' }}>
                    {pauseLoading ? '…' : `Pause ${days} day${days > 1 ? 's' : ''}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: 32 }}>
              <label style={m.label}>Your name</label>
              <input className="mp-input" type="text" placeholder="Your first name" value={profileName}
                onChange={e => setProfileName(e.target.value)} maxLength={40}
                style={{ ...m.input, maxWidth: 320 }} />
              <p style={m.hint}>Used to personalize your digest greeting.</p>
            </div>

            {/* Email (read-only) */}
            <div style={{ marginBottom: 32 }}>
              <label style={m.label}>Email address</label>
              <div style={{ ...m.input, maxWidth: 380, color: '#475569', cursor: 'default', display: 'flex', alignItems: 'center' }}>
                {email || '—'}
              </div>
              <p style={m.hint}>To change your email, delete this account and re-subscribe.</p>
            </div>

            {/* Danger zone */}
            <div style={{ borderTop: '1px solid #1e3a5f', paddingTop: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', marginBottom: 12 }}>Danger zone</h3>
              {!showDeleteConfirm ? (
                <button onClick={() => setShowDeleteConfirm(true)}
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 }}>
                  Delete account & unsubscribe
                </button>
              ) : (
                <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '20px 24px' }}>
                  <p style={{ fontSize: 14, color: '#f87171', marginBottom: 16, lineHeight: 1.6 }}>
                    This will permanently delete your account and stop all future digests. This cannot be undone.
                  </p>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={handleDeleteAccount} disabled={loading}
                      style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700 }}>
                      {loading ? 'Deleting…' : 'Yes, delete my account'}
                    </button>
                    <button onClick={() => setShowDeleteConfirm(false)}
                      style={{ background: 'none', border: '1px solid #1e3a5f', color: '#64748b', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sticky save bar */}
        <div style={m.stickyBar}>
          {saveMsg && <span className="save-success" style={m.saveSuccess}>{saveMsg}</span>}
          {error && <span style={{ fontSize: 13, color: '#f87171' }}>{error}</span>}
          {(activeSection === 'topics' || activeSection === 'time' || activeSection === 'account') && (
            <button className="mp-btn-primary" onClick={handleSave} disabled={loading || topics.length === 0}
              style={{ ...m.saveBtn, opacity: loading || topics.length === 0 ? 0.4 : 1 }}>
              {loading ? 'Saving…' : '💾 Save changes'}
            </button>
          )}
        </div>
      </div>
    </div></>
  );
}

/* ─── Styles ──────────────────────────────────────────────────────── */
const m = {
  page: {
    minHeight: '100vh', background: '#060d1f',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px', fontFamily: "'Inter', system-ui, sans-serif",
    position: 'relative', overflow: 'hidden',
  },
  dashPage: { minHeight: '100vh', background: '#060d1f', fontFamily: "'Inter', system-ui, sans-serif", color: '#f1f5f9', position: 'relative' },
  orb1: { position: 'fixed', top: '5%', left: '10%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(14,165,233,0.08) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  orb2: { position: 'fixed', bottom: '10%', right: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle,rgba(99,102,241,0.07) 0%,transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none', zIndex: 0 },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid #1e293b', borderRadius: 24, padding: 'clamp(32px,5vw,48px)', maxWidth: 460, width: '100%', position: 'relative', zIndex: 1, boxShadow: '0 40px 80px rgba(0,0,0,0.4)' },
  backLink: { background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, padding: '0 0 20px 0', display: 'block' },
  cardTitle: { fontSize: 28, fontWeight: 800, color: '#f8fafc', marginBottom: 10 },
  cardSub:   { fontSize: 15, color: '#64748b', lineHeight: 1.7, marginBottom: 28 },
  fieldGroup:{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 },
  label:     { fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginBottom: 8 },
  input: { width: '100%', padding: '14px 16px', fontSize: 16, fontWeight: 500, background: '#0a1628', border: '1.5px solid #1e3a5f', borderRadius: 12, fontFamily: 'inherit', color: '#f1f5f9', transition: 'all 0.2s', boxSizing: 'border-box' },
  hint:      { fontSize: 12, color: '#334155', margin: '6px 0 0' },
  primaryBtn:{ width: '100%', padding: '14px', fontSize: 15, fontWeight: 700, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', borderRadius: 12, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 4px 20px rgba(14,165,233,0.3)', marginBottom: 16 },
  errorBox:  { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', color: '#f87171', fontSize: 14, marginBottom: 16 },
  legalNote: { fontSize: 12, color: '#334155', textAlign: 'center', lineHeight: 1.7, margin: 0 },
  textLink:  { background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, textDecoration: 'underline' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', borderBottom: '1px solid #0d1829', background: 'rgba(6,13,31,0.9)', backdropFilter: 'blur(16px)', position: 'sticky', top: 0, zIndex: 100 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  logo:       { fontSize: 28 },
  headerTitle:{ fontSize: 16, fontWeight: 800, color: '#f8fafc' },
  headerSub:  { fontSize: 11, color: '#334155' },
  headerRight:{ display: 'flex', alignItems: 'center', gap: 12 },
  phoneBadge: { fontSize: 13, color: '#475569', background: '#0a1628', border: '1px solid #1e3a5f', borderRadius: 8, padding: '4px 12px' },
  logoutBtn:  { background: 'none', border: '1px solid #1e3a5f', color: '#475569', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 },
  dashBody:   { maxWidth: 900, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(16px,4vw,32px) 100px', position: 'relative', zIndex: 1 },
  welcomeRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 32, flexWrap: 'wrap' },
  welcomeTitle:{ fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 800, color: '#f8fafc', marginBottom: 8 },
  welcomeSub:  { fontSize: 14, color: '#64748b' },
  saveBtn:    { padding: '12px 24px', fontSize: 14, fontWeight: 700, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 4px 16px rgba(14,165,233,0.3)', whiteSpace: 'nowrap' },
  saveSuccess:{ fontSize: 13, color: '#22c55e', fontWeight: 600 },
  tabs:       { display: 'flex', gap: 0, borderBottom: '1px solid #1e3a5f', marginBottom: 32, overflowX: 'auto' },
  tab:        { background: 'none', border: 'none', padding: '12px 20px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', transition: 'color 0.15s' },
  sectionHeader: { display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 },
  sectionTitle:  { fontSize: 20, fontWeight: 800, color: '#f8fafc' },
  sectionSub:    { fontSize: 13, color: '#475569' },
  capBanner:  { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#fbbf24' },
  stickyBar:  { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(6,13,31,0.96)', backdropFilter: 'blur(16px)', borderTop: '1px solid #0d1829', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, zIndex: 50 },
};
